/**
 * GearMenu — unified settings/actions dropdown used in both the chat view
 * (top-bar ⚙, drops downward) and the session list view (bottom-right FAB,
 * rises upward). Parent is responsible for open/close state and positioning
 * the wrapper as `position: relative`.
 */

import { DevPreviewButton } from './DevPreviewButton';
import { useSwipeDownToClose } from '../hooks/useSwipeDownToClose';

interface Props {
  /** Opens downward from a top trigger vs upward from a bottom FAB */
  placement?: 'top' | 'bottom' | 'sheet';
  userEmail?: string | null;
  onClose: () => void;

  // ── Context flags ──────────────────────────────────────────────
  inSession?: boolean;
  isAdmin?: boolean;
  memosEnabled?: boolean;
  lessonGeneratorEnabled?: boolean;
  rebuildEnabled?: boolean;
  /** Profile-flag gates for previously always-on entries (PR adds these). Default true.
   *  guideEnabled reuses the existing profile.features.guide_enabled flag (G3 choice). */
  guideEnabled?: boolean;
  pulseEnabled?: boolean;
  billingEnabled?: boolean;

  // ── Utility toggles (chat-only) ───────────────────────────────
  keyboardStripOpen?: boolean;
  onToggleKeyboard?: () => void;

  // ── SSH ───────────────────────────────────────────────────────
  sshConnected?: boolean;
  onSshConnect?: () => void;
  onSshDisconnect?: () => void;

  // ── Session actions (chat-only) ────────────────────────────────
  onResetSession?: () => void;
  onDeleteSession?: () => void;

  // ── Navigation ────────────────────────────────────────────────
  onSettings?: () => void;
  onBrowser?: () => void;
  onFiles?: () => void;
  /** Opens the Usage drawer (UsageTab) — moved here from the bottom nav (issue #4
   *  declutter, coach-tooltip-system.md Phase 5). */
  onUsage?: () => void;
  onMemos?: () => void;
  onLessons?: () => void;
  onSessionsDrawer?: () => void;
  onLogbook?: () => void;

  // ── Admin ─────────────────────────────────────────────────────
  onDoctor?: () => void;
  onRebuild?: () => void;
  onActAs?: () => void;
  actAsPersona?: string | null;
  /** Dev-preview HMR-from-worktree button (#1403) — admin + prod-only. When true
   *  (and isAdmin), renders <DevPreviewButton> in the Admin section keyed to the
   *  current chat session's worktree. */
  devPreviewEnabled?: boolean;
  devPreviewSessionId?: string | null;

  // ── WASM ──────────────────────────────────────────────────────
  wasmActive?: boolean;

  // ── Account ───────────────────────────────────────────────────
  onSignOut?: () => void;

  // ── Coach replay (coach-tooltip-system.md §5.4) ────────────────
  /** Replays the persona intro + re-arms the first-run coach rows. Lives here
   *  (More/Gear), never in the bottom nav — issue #4 (nav crowding). */
  onReplayCoach?: () => void;
}

export function GearMenu({
  placement = 'top',
  userEmail,
  onClose,
  inSession,
  isAdmin,
  memosEnabled,
  lessonGeneratorEnabled,
  rebuildEnabled,
  guideEnabled = true,
  pulseEnabled = true,
  billingEnabled,
  keyboardStripOpen,
  onToggleKeyboard,
  sshConnected,
  onSshConnect,
  onSshDisconnect,
  onResetSession,
  onDeleteSession,
  onSettings,
  onBrowser,
  onFiles,
  onUsage,
  onMemos,
  onLessons,
  onSessionsDrawer,
  onLogbook,
  onDoctor,
  onRebuild,
  onActAs,
  actAsPersona,
  devPreviewEnabled,
  devPreviewSessionId,
  onSignOut,
  wasmActive,
  onReplayCoach,
}: Props) {
  const act = (fn?: () => void) => () => { onClose(); if (fn) setTimeout(fn, 0); };

  // unified-drawer P2: swipe-down-to-close when rendered as a bottom sheet (More menu).
  const sheetSwipe = useSwipeDownToClose(onClose);

  // unified-drawer §8 declutter: in the CHAT-view sheet the cluster row (just above
  // the composer) already exposes Browser · Dev-Preview · SSH · ⚙Settings as the
  // fast path, so drop those duplicate entries from More here. In list view there is
  // NO cluster row, so they stay (the only way to reach them). Gated on placement.
  const inCluster = placement === 'sheet';

  const showSsh = (onSshConnect !== undefined || onSshDisconnect !== undefined) && !inCluster;
  const showUtilitySection = onToggleKeyboard || showSsh;
  const showSessionSection = inSession && (onResetSession || onDeleteSession);

  return (
    <div
      class={`gear-menu gear-menu--${placement}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* unified-drawer P2: swipe-down handle — only when this menu is a bottom sheet */}
      {placement === 'sheet' && (
        <div class="drawer-grab" aria-hidden="true" onTouchStart={sheetSwipe.onTouchStart} onTouchEnd={sheetSwipe.onTouchEnd} />
      )}

      {/* Email header */}
      {userEmail !== undefined && (
        <div class="sl-settings-email">
          {userEmail || 'Not signed in'}
          {wasmActive && <span class="gear-wasm-badge" title="WASM compute module active">WASM</span>}
        </div>
      )}

      {/* ── Utility toggles (chat-only) ── */}
      {showUtilitySection && (
        <>
          {onToggleKeyboard && (
            <button class="sl-settings-menu-btn" onClick={act(onToggleKeyboard)}>
              <span class="sl-settings-btn-icon">⌨</span>
              {keyboardStripOpen ? 'Hide toolbar' : 'Quick toolbar'}
            </button>
          )}
          {showSsh && (
            <button
              class={`sl-settings-menu-btn${sshConnected ? ' sl-settings-menu-btn--ssh-on' : ''}`}
              onClick={act(() => sshConnected ? onSshDisconnect?.() : onSshConnect?.())}
            >
              <span class="sl-settings-btn-icon">{sshConnected ? '⏏' : '🖥'}</span>
              {sshConnected ? 'Disconnect SSH' : 'Connect SSH'}
            </button>
          )}
          <div class="sl-settings-sep" />
        </>
      )}

      {/* ── Session actions (chat-only) ── */}
      {showSessionSection && (
        <>
          {onResetSession && (
            <button class="sl-settings-menu-btn" onClick={act(onResetSession)}>
              <span class="sl-settings-btn-icon">↺</span> Reset session
            </button>
          )}
          {onDeleteSession && (
            <button class="sl-settings-menu-btn sl-settings-menu-btn--danger" onClick={act(onDeleteSession)}>
              <span class="sl-settings-btn-icon">🗑</span> Delete session
            </button>
          )}
          <div class="sl-settings-sep" />
        </>
      )}

      {/* ── Navigation ── */}
      {/* Settings + Browser dropped from the chat-view sheet (in the cluster row); kept in list view. */}
      {onSettings && !inCluster && (
        <button class="sl-settings-menu-btn" onClick={act(onSettings)}>
          <span class="sl-settings-btn-icon">⚙</span> Settings
        </button>
      )}
      {onBrowser && !inCluster && (
        <button class="sl-settings-menu-btn" onClick={act(onBrowser)}>
          <span class="sl-settings-btn-icon">🌐</span> Browser
        </button>
      )}
      {onFiles && (
        <button class="sl-settings-menu-btn" onClick={act(onFiles)}>
          <span class="sl-settings-btn-icon">📁</span> Files
        </button>
      )}
      {onUsage && (
        <button class="sl-settings-menu-btn" onClick={act(onUsage)}>
          <span class="sl-settings-btn-icon">📊</span> Usage
        </button>
      )}
      {memosEnabled && onMemos && (
        <button class="sl-settings-menu-btn" onClick={act(onMemos)}>
          <span class="sl-settings-btn-icon">📋</span> Memos
        </button>
      )}
      {lessonGeneratorEnabled && onLessons && (
        <button class="sl-settings-menu-btn" onClick={act(onLessons)}>
          <span class="sl-settings-btn-icon">📚</span> Lessons
        </button>
      )}
      {onSessionsDrawer && (
        <button class="sl-settings-menu-btn" onClick={act(onSessionsDrawer)}>
          <span class="sl-settings-btn-icon">🗂</span> Sessions
        </button>
      )}
      {onLogbook && (
        <button class="sl-settings-menu-btn" onClick={act(onLogbook)}>
          <span class="sl-settings-btn-icon">🐞</span> Logbook
        </button>
      )}
      {guideEnabled && (
        <button class="sl-settings-menu-btn" onClick={act(() => { window.location.href = '/guide'; })}>
          <span class="sl-settings-btn-icon">📖</span> Guide
        </button>
      )}
      {pulseEnabled && (
        <button class="sl-settings-menu-btn" onClick={act(() => { window.location.href = '/monitor'; })}>
          <span class="sl-settings-btn-icon">📡</span> Pulse
        </button>
      )}
      {billingEnabled && (
        <button class="sl-settings-menu-btn" onClick={act(() => { window.location.href = '/portal'; })}>
          <span class="sl-settings-btn-icon">💳</span> Credits &amp; billing
        </button>
      )}
      {onReplayCoach && (
        <button class="sl-settings-menu-btn" onClick={act(onReplayCoach)}>
          <span class="sl-settings-btn-icon">?</span> Replay intro
        </button>
      )}

      {/* ── Admin (gated) ── */}
      {isAdmin && (
        <>
          <div class="sl-settings-sep" />
          <div class="sl-settings-label">Admin</div>
          <button class="sl-settings-menu-btn" onClick={act(() => { window.location.href = '/admin'; })}>
            <span class="sl-settings-btn-icon">🔑</span> Admin Panel
          </button>
          <button class="sl-settings-menu-btn" onClick={act(() => { window.location.href = '/arch'; })}>
            <span class="sl-settings-btn-icon">🗺️</span> Architecture Map
          </button>
          {onDoctor && (
            <button class="sl-settings-menu-btn" onClick={act(onDoctor)}>
              <span class="sl-settings-btn-icon">🩺</span> Diagnostics
            </button>
          )}
          {onRebuild && rebuildEnabled && (
            <button class="sl-settings-menu-btn" onClick={act(onRebuild)}>
              <span class="sl-settings-btn-icon">🔄</span> Rebuild Backend
            </button>
          )}
          {onActAs && (
            <button class="sl-settings-menu-btn" onClick={act(onActAs)}>
              <span class="sl-settings-btn-icon">🎭</span>
              {actAsPersona ? `Acting as: ${actAsPersona}` : 'Act as persona…'}
            </button>
          )}
          {devPreviewEnabled && !inCluster && (
            <DevPreviewButton sessionId={devPreviewSessionId ?? null} />
          )}
        </>
      )}

      {/* ── Account ── */}
      <div class="sl-settings-sep" />
      {onSettings && (
        <button class="sl-settings-menu-btn" onClick={act(onSettings)}>
          <span class="sl-settings-btn-icon">💳</span> Billing
        </button>
      )}
      <button class="sl-settings-menu-btn sl-settings-menu-btn--muted" disabled title="Coming soon">
        <span class="sl-settings-btn-icon">🗑</span> Delete account
      </button>

      {/* ── Sign out ── */}
      {onSignOut && (
        <>
          <div class="sl-settings-sep" />
          <button class="sl-settings-menu-btn sl-settings-menu-btn--danger" onClick={act(onSignOut)}>
            <span class="sl-settings-btn-icon">→</span> Sign out
          </button>
        </>
      )}
    </div>
  );
}
